import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, Plus, Package, ArrowDownCircle, AlertTriangle, CheckCircle,
  RefreshCw, Search, X, ChevronDown, ChevronUp, Flower2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;

const STATUS_STYLE = {
  available: "bg-green-50 text-green-700 border-green-200",
  low_stock: "bg-amber-50 text-amber-700 border-amber-200",
  out_of_stock: "bg-red-50 text-red-700 border-red-200",
};

export default function AdminInventory() {
  const [flowers, setFlowers] = useState([]);
  const [stockIns, setStockIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStockIn, setShowStockIn] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    flower_code: "", flower_name: "", color: "",
    qty: "", unit_cost: "", supplier: "", notes: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("stock"); // "stock" | "history"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/inventory/flowers`, { withCredentials: true }),
        axios.get(`${API}/admin/inventory/stock-in`, { withCredentials: true })
      ]);
      setFlowers(fRes.data.flowers || []);
      setStockIns(sRes.data.records || []);
    } catch {
      toast.error("Gagal memuat data inventory");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitStockIn = async () => {
    if (!form.flower_code || !form.qty) {
      toast.error("Kode bunga dan jumlah wajib diisi");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/inventory/stock-in`, {
        ...form,
        qty: parseInt(form.qty),
        unit_cost: parseFloat(form.unit_cost || 0)
      }, { withCredentials: true });
      toast.success(`Stok masuk ${form.flower_name || form.flower_code} berhasil dicatat`);
      setForm({ flower_code: "", flower_name: "", color: "", qty: "", unit_cost: "", supplier: "", notes: "" });
      setShowStockIn(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mencatat stok masuk");
    }
    setSubmitting(false);
  };

  const filtered = flowers.filter(f =>
    !search ||
    f.flower_name?.toLowerCase().includes(search.toLowerCase()) ||
    f.flower_code?.toLowerCase().includes(search.toLowerCase())
  );

  const lowStock = flowers.filter(f => f.qty > 0 && f.qty <= 10).length;
  const outOfStock = flowers.filter(f => f.qty <= 0).length;
  const totalQty = flowers.reduce((sum, f) => sum + (f.qty || 0), 0);

  return (
    <div data-testid="admin-inventory">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl text-[#1C1A19]">Inventory <span className="text-seikat-pink">Bunga</span></h1>
          <p className="font-body text-sm text-[#8A857D] mt-1">Kelola stok bunga masuk & keluar</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="w-9 h-9 rounded-xl border border-[#D4859B]/20 flex items-center justify-center text-[#8A857D] hover:text-seikat-pink transition-colors" data-testid="inventory-refresh">
            <RefreshCw size={15} />
          </button>
          <button
            onClick={() => setShowStockIn(true)}
            data-testid="btn-add-stock-in"
            className="flex items-center gap-2 px-4 py-2 bg-seikat-pink text-white rounded-xl text-sm font-body font-medium hover:bg-seikat-pink/90 transition-colors"
          >
            <Plus size={14} /> Stok Masuk
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total Jenis Bunga", value: flowers.length, icon: Flower2, color: "#D4859B" },
          { label: "Total Stok (unit)", value: totalQty.toLocaleString("id-ID"), icon: Package, color: "#C5DB7B" },
          { label: "Hampir Habis", value: lowStock, icon: AlertTriangle, color: "#E8A87C" },
          { label: "Stok Habis", value: outOfStock, icon: X, color: "#E07070" },
        ].map((c, i) => (
          <motion.div key={c.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: `${c.color}22` }}>
                <c.icon size={13} style={{ color: c.color }} />
              </div>
            </div>
            <p className="font-heading text-2xl text-[#1C1A19]">{c.value}</p>
            <p className="font-body text-[10px] tracking-widest uppercase text-[#A8A29A] mt-1">{c.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-[#FDF8F6] border border-[#D4859B]/15 rounded-2xl w-fit mb-6">
        {[{ id: "stock", label: "Stok Saat Ini" }, { id: "history", label: "Riwayat Masuk" }].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}
            className={`px-5 py-2 rounded-xl text-sm font-body transition-all ${activeTab === t.id ? "bg-seikat-pink text-white shadow-sm" : "text-[#4A453F] hover:text-[#1C1A19]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-seikat-pink" size={28} /></div>
      ) : activeTab === "stock" ? (
        <>
          {/* Search */}
          <div className="relative mb-4 max-w-sm">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A8A29A]" />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Cari bunga..." data-testid="inventory-search"
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-[#D4859B]/20 rounded-xl text-sm font-body text-[#1C1A19] placeholder:text-[#A8A29A] outline-none focus:border-seikat-pink/50"
            />
          </div>
          <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm overflow-hidden">
            <table className="w-full" data-testid="inventory-table">
              <thead>
                <tr className="border-b border-[#D4859B]/12 bg-[#FDF8F6]">
                  {["Kode Bunga", "Nama", "Warna", "Stok (unit)", "Harga Satuan", "Status"].map(h => (
                    <th key={h} className="text-left px-4 py-3 font-body text-[10px] tracking-widest uppercase text-[#A8A29A]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-16 font-body text-[#A8A29A]">
                    <Package size={28} className="mx-auto mb-2 text-[#D4859B]/30" />
                    <p>Belum ada data inventory</p>
                    <p className="text-xs mt-1">Tambah stok masuk pertama untuk mulai melacak</p>
                  </td></tr>
                ) : filtered.map((f, i) => {
                  const status = f.qty <= 0 ? "out_of_stock" : f.qty <= 10 ? "low_stock" : "available";
                  return (
                    <tr key={f.flower_code} className={`border-b border-[#D4859B]/8 hover:bg-[#FDF8F6] transition-colors ${i % 2 === 0 ? "" : "bg-[#FDFAF8]"}`}>
                      <td className="px-4 py-3 font-mono text-xs text-[#4A453F]">{f.flower_code}</td>
                      <td className="px-4 py-3 font-body text-sm font-medium text-[#1C1A19]">{f.flower_name}</td>
                      <td className="px-4 py-3 font-body text-sm text-[#6A6560]">{f.color || "—"}</td>
                      <td className="px-4 py-3 font-body text-sm font-semibold text-[#1C1A19]">{(f.qty || 0).toLocaleString("id-ID")}</td>
                      <td className="px-4 py-3 font-body text-sm text-[#6A6560]">{f.price ? fmt(f.price) : "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full border text-[10px] font-body uppercase tracking-wide ${STATUS_STYLE[status]}`}>
                          {status === "available" ? "Tersedia" : status === "low_stock" ? "Hampir Habis" : "Habis"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        /* History tab */
        <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm overflow-hidden">
          <table className="w-full" data-testid="stock-in-table">
            <thead>
              <tr className="border-b border-[#D4859B]/12 bg-[#FDF8F6]">
                {["Tanggal", "Kode Bunga", "Nama", "Qty Masuk", "Harga Satuan", "Supplier", "Catatan"].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-body text-[10px] tracking-widest uppercase text-[#A8A29A]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stockIns.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-16 font-body text-[#A8A29A]">
                  <ArrowDownCircle size={28} className="mx-auto mb-2 text-[#D4859B]/30" />
                  <p>Belum ada riwayat stok masuk</p>
                </td></tr>
              ) : stockIns.map((r, i) => (
                <tr key={r.stock_in_id} className={`border-b border-[#D4859B]/8 hover:bg-[#FDF8F6] transition-colors ${i % 2 === 0 ? "" : "bg-[#FDFAF8]"}`}>
                  <td className="px-4 py-3 font-body text-xs text-[#6A6560]">{r.received_at ? new Date(r.received_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#4A453F]">{r.flower_code}</td>
                  <td className="px-4 py-3 font-body text-sm font-medium text-[#1C1A19]">{r.flower_name}</td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1 text-green-700 font-body text-sm font-semibold">
                      <ChevronUp size={13} />{r.qty}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-body text-sm text-[#6A6560]">{r.unit_cost ? fmt(r.unit_cost) : "—"}</td>
                  <td className="px-4 py-3 font-body text-sm text-[#6A6560]">{r.supplier || "—"}</td>
                  <td className="px-4 py-3 font-body text-xs text-[#8A857D]">{r.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Stock-In Modal */}
      <AnimatePresence>
        {showStockIn && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1C1A19]/60 backdrop-blur-sm"
            onClick={e => e.target === e.currentTarget && setShowStockIn(false)}
            data-testid="stock-in-modal"
          >
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading text-2xl text-[#1C1A19]">Catat <em className="text-seikat-pink">Stok Masuk</em></h2>
                  <p className="font-body text-xs text-[#A8A29A] mt-1">Barang masuk dari supplier atau pengadaan</p>
                </div>
                <button onClick={() => setShowStockIn(false)} className="w-8 h-8 rounded-full hover:bg-seikat-pink/10 flex items-center justify-center">
                  <X size={15} className="text-[#4A453F]" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] tracking-widest uppercase text-[#8A857D] mb-1.5">Kode Bunga *</label>
                    <input value={form.flower_code} onChange={e => setForm({ ...form, flower_code: e.target.value })}
                      placeholder="rose_red" data-testid="sin-flower-code"
                      className="w-full px-3 py-2.5 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body text-[#1C1A19] outline-none focus:border-seikat-pink/50" />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-widest uppercase text-[#8A857D] mb-1.5">Nama Bunga</label>
                    <input value={form.flower_name} onChange={e => setForm({ ...form, flower_name: e.target.value })}
                      placeholder="Red Rose" data-testid="sin-flower-name"
                      className="w-full px-3 py-2.5 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body text-[#1C1A19] outline-none focus:border-seikat-pink/50" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] tracking-widest uppercase text-[#8A857D] mb-1.5">Jumlah (qty) *</label>
                    <input type="number" min="1" value={form.qty} onChange={e => setForm({ ...form, qty: e.target.value })}
                      placeholder="100" data-testid="sin-qty"
                      className="w-full px-3 py-2.5 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body text-[#1C1A19] outline-none focus:border-seikat-pink/50" />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-widest uppercase text-[#8A857D] mb-1.5">Harga Satuan (Rp)</label>
                    <input type="number" min="0" value={form.unit_cost} onChange={e => setForm({ ...form, unit_cost: e.target.value })}
                      placeholder="5000" data-testid="sin-unit-cost"
                      className="w-full px-3 py-2.5 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body text-[#1C1A19] outline-none focus:border-seikat-pink/50" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] tracking-widest uppercase text-[#8A857D] mb-1.5">Warna</label>
                    <input value={form.color} onChange={e => setForm({ ...form, color: e.target.value })}
                      placeholder="red" data-testid="sin-color"
                      className="w-full px-3 py-2.5 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body text-[#1C1A19] outline-none focus:border-seikat-pink/50" />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-widest uppercase text-[#8A857D] mb-1.5">Supplier</label>
                    <input value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })}
                      placeholder="PT Bunga Segar" data-testid="sin-supplier"
                      className="w-full px-3 py-2.5 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body text-[#1C1A19] outline-none focus:border-seikat-pink/50" />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[#8A857D] mb-1.5">Catatan</label>
                  <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                    placeholder="Kondisi barang, batch, dll..."
                    className="w-full px-3 py-2.5 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body text-[#1C1A19] outline-none focus:border-seikat-pink/50 resize-none" />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowStockIn(false)}
                  className="flex-1 px-4 py-2.5 border border-[#D4859B]/20 rounded-xl text-sm font-body text-[#4A453F] hover:bg-[#FDF8F6] transition-colors">
                  Batal
                </button>
                <button onClick={submitStockIn} disabled={submitting} data-testid="submit-stock-in"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-seikat-pink text-white rounded-xl text-sm font-body font-medium hover:bg-seikat-pink/90 transition-colors disabled:opacity-60">
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                  Simpan Stok Masuk
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
